import React, { useState, useEffect, useCallback } from 'react';
import { caAuditAPI } from '../../services/api';
import Navbar from '../../components/Navbar';
import './CAAuditReports.css';

const CAAuditReports = () => {
  // Active Tab State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Date Range Filters (common across reports)
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Dashboard State
  const [dashboardData, setDashboardData] = useState(null);

  // GSTR-1 State
  const [gstr1Data, setGstr1Data] = useState(null);
  const [gstr1View, setGstr1View] = useState('summary'); // summary, b2b, b2c, stateWise

  // GSTR-3B State
  const [gstr3bData, setGstr3bData] = useState(null);

  // TDS State
  const [tdsData, setTdsData] = useState(null);
  const [tdsView, setTdsView] = useState('register'); // register, deductees, quarterly, certificates
  const [tdsQuarter, setTdsQuarter] = useState('');
  const [tdsYear, setTdsYear] = useState(new Date().getFullYear());

  // HSN/SAC State
  const [hsnSacData, setHsnSacData] = useState(null);
  const [hsnSacView, setHsnSacView] = useState('summary'); // summary, rateWise

  // Registers State
  const [registerData, setRegisterData] = useState(null);
  const [registerType, setRegisterType] = useState('sales'); // sales, settlements, creditNotes, debitNotes, dpEarnings

  // Settlement Reconciliation State
  const [settlementData, setSettlementData] = useState(null);
  const [settlementView, setSettlementView] = useState('reconciliation'); // reconciliation, discrepancies, bankTracking

  // Round-off State
  const [roundOffData, setRoundOffData] = useState(null);
  const [roundOffView, setRoundOffView] = useState('report'); // report, summary
  const [roundOffGroupBy, setRoundOffGroupBy] = useState('day');

  // Revenue Recognition State
  const [revenueData, setRevenueData] = useState(null);
  const [revenueView, setRevenueView] = useState('recognition'); // recognition, cutoff, deferred
  const [revenueGroupBy, setRevenueGroupBy] = useState('day');

  // Trial Balance & P&L State
  const [trialBalanceData, setTrialBalanceData] = useState(null);
  const [plData, setPlData] = useState(null);
  const [financialView, setFinancialView] = useState('trialBalance'); // trialBalance, profitLoss

  // Pagination State
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });

  // Financial Year Helper
  const getCurrentFinancialYear = () => {
    const now = new Date();
    const month = now.getMonth() + 1;
    return month >= 4 ? now.getFullYear() : now.getFullYear() - 1;
  };

  // ============================================================
  // API CALLS
  // ============================================================

  // Dashboard
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await caAuditAPI.getDashboard({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      setDashboardData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      setMessage({ type: 'error', text: 'Failed to load dashboard data' });
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // GSTR-1
  const fetchGSTR1 = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      switch (gstr1View) {
        case 'b2b':
          response = await caAuditAPI.getGSTR1B2B({ ...dateRange, page: pagination.page, limit: pagination.limit });
          break;
        case 'b2c':
          response = await caAuditAPI.getGSTR1B2C({ ...dateRange, page: pagination.page, limit: pagination.limit });
          break;
        case 'stateWise':
          response = await caAuditAPI.getGSTR1StateWise({ ...dateRange });
          break;
        default:
          response = await caAuditAPI.getGSTR1({ ...dateRange });
      }
      setGstr1Data(response.data.data);
      if (response.data.data.pagination) {
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch GSTR-1:', error);
      setMessage({ type: 'error', text: 'Failed to load GSTR-1 data' });
    } finally {
      setLoading(false);
    }
  }, [dateRange, gstr1View, pagination.page, pagination.limit]);

  // GSTR-3B
  const fetchGSTR3B = useCallback(async () => {
    setLoading(true);
    try {
      const response = await caAuditAPI.getGSTR3B({ ...dateRange });
      setGstr3bData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch GSTR-3B:', error);
      setMessage({ type: 'error', text: 'Failed to load GSTR-3B data' });
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // TDS
  const fetchTDS = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      switch (tdsView) {
        case 'deductees':
          response = await caAuditAPI.getTDSDeductees({ ...dateRange, page: pagination.page, limit: pagination.limit });
          break;
        case 'quarterly':
          response = await caAuditAPI.getTDSQuarterly({ financialYear: tdsYear });
          break;
        case 'certificates':
          response = await caAuditAPI.getTDSCertificates({ ...dateRange });
          break;
        default:
          response = await caAuditAPI.getTDSRegister({ ...dateRange, page: pagination.page, limit: pagination.limit });
      }
      setTdsData(response.data.data);
      if (response.data.data.pagination) {
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch TDS:', error);
      setMessage({ type: 'error', text: 'Failed to load TDS data' });
    } finally {
      setLoading(false);
    }
  }, [dateRange, tdsView, tdsYear, pagination.page, pagination.limit]);

  // HSN/SAC
  const fetchHSNSAC = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      if (hsnSacView === 'rateWise') {
        response = await caAuditAPI.getHSNSACRateWise({ ...dateRange });
      } else {
        response = await caAuditAPI.getHSNSACSummary({ ...dateRange, page: pagination.page, limit: pagination.limit });
      }
      setHsnSacData(response.data.data);
      if (response.data.data.pagination) {
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch HSN/SAC:', error);
      setMessage({ type: 'error', text: 'Failed to load HSN/SAC data' });
    } finally {
      setLoading(false);
    }
  }, [dateRange, hsnSacView, pagination.page, pagination.limit]);

  // Registers
  const fetchRegisters = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      switch (registerType) {
        case 'settlements':
          response = await caAuditAPI.getSettlementRegister({ ...dateRange, page: pagination.page, limit: pagination.limit });
          break;
        case 'creditNotes':
          response = await caAuditAPI.getCreditNoteRegister({ ...dateRange, page: pagination.page, limit: pagination.limit });
          break;
        case 'debitNotes':
          response = await caAuditAPI.getDebitNoteRegister({ ...dateRange, page: pagination.page, limit: pagination.limit });
          break;
        case 'dpEarnings':
          response = await caAuditAPI.getDPEarningsRegister({ ...dateRange, page: pagination.page, limit: pagination.limit });
          break;
        default:
          response = await caAuditAPI.getSalesRegister({ ...dateRange, page: pagination.page, limit: pagination.limit });
      }
      setRegisterData(response.data.data);
      if (response.data.data.pagination) {
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch registers:', error);
      setMessage({ type: 'error', text: 'Failed to load register data' });
    } finally {
      setLoading(false);
    }
  }, [dateRange, registerType, pagination.page, pagination.limit]);

  // Settlement Reconciliation
  const fetchSettlement = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      switch (settlementView) {
        case 'discrepancies':
          response = await caAuditAPI.getSettlementDiscrepancies({ ...dateRange, page: pagination.page, limit: pagination.limit });
          break;
        case 'bankTracking':
          response = await caAuditAPI.getBankTracking({ ...dateRange, page: pagination.page, limit: pagination.limit });
          break;
        default:
          response = await caAuditAPI.getSettlementReconciliation({ ...dateRange, page: pagination.page, limit: pagination.limit });
      }
      setSettlementData(response.data.data);
      if (response.data.data.pagination) {
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch settlement:', error);
      setMessage({ type: 'error', text: 'Failed to load settlement data' });
    } finally {
      setLoading(false);
    }
  }, [dateRange, settlementView, pagination.page, pagination.limit]);

  // Round-off
  const fetchRoundOff = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      if (roundOffView === 'summary') {
        response = await caAuditAPI.getRoundOffSummary({ ...dateRange, groupBy: roundOffGroupBy });
      } else {
        response = await caAuditAPI.getRoundOffReport({ ...dateRange, page: pagination.page, limit: pagination.limit });
      }
      setRoundOffData(response.data.data);
      if (response.data.data.pagination) {
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch round-off:', error);
      setMessage({ type: 'error', text: 'Failed to load round-off data' });
    } finally {
      setLoading(false);
    }
  }, [dateRange, roundOffView, roundOffGroupBy, pagination.page, pagination.limit]);

  // Revenue Recognition
  const fetchRevenue = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      switch (revenueView) {
        case 'cutoff':
          response = await caAuditAPI.getRevenueCutoff({ asOfDate: dateRange.endDate });
          break;
        case 'deferred':
          response = await caAuditAPI.getDeferredRevenue({ asOfDate: dateRange.endDate });
          break;
        default:
          response = await caAuditAPI.getRevenueRecognition({ ...dateRange, groupBy: revenueGroupBy });
      }
      setRevenueData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch revenue:', error);
      setMessage({ type: 'error', text: 'Failed to load revenue data' });
    } finally {
      setLoading(false);
    }
  }, [dateRange, revenueView, revenueGroupBy]);

  // Trial Balance & P&L
  const fetchFinancials = useCallback(async () => {
    setLoading(true);
    try {
      if (financialView === 'profitLoss') {
        const response = await caAuditAPI.getProfitLoss({ ...dateRange });
        setPlData(response.data.data);
      } else {
        const response = await caAuditAPI.getTrialBalance({ asOfDate: dateRange.endDate });
        setTrialBalanceData(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch financials:', error);
      setMessage({ type: 'error', text: 'Failed to load financial data' });
    } finally {
      setLoading(false);
    }
  }, [dateRange, financialView]);

  // Export Excel Workbook
  const handleExportWorkbook = async () => {
    setLoading(true);
    try {
      const response = await caAuditAPI.exportExcelWorkbook({ ...dateRange });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CA_Audit_Report_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'Excel workbook downloaded successfully' });
    } catch (error) {
      console.error('Failed to export workbook:', error);
      setMessage({ type: 'error', text: 'Failed to export Excel workbook' });
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // EFFECTS
  // ============================================================

  useEffect(() => {
    const fetchData = () => {
      setPagination(prev => ({ ...prev, page: 1 }));
      switch (activeTab) {
        case 'dashboard':
          fetchDashboard();
          break;
        case 'gstr1':
          fetchGSTR1();
          break;
        case 'gstr3b':
          fetchGSTR3B();
          break;
        case 'tds':
          fetchTDS();
          break;
        case 'hsnSac':
          fetchHSNSAC();
          break;
        case 'registers':
          fetchRegisters();
          break;
        case 'settlement':
          fetchSettlement();
          break;
        case 'roundOff':
          fetchRoundOff();
          break;
        case 'revenue':
          fetchRevenue();
          break;
        case 'financials':
          fetchFinancials();
          break;
        default:
          break;
      }
    };
    fetchData();
  }, [activeTab, dateRange, fetchDashboard, fetchGSTR1, fetchGSTR3B, fetchTDS, fetchHSNSAC, fetchRegisters, fetchSettlement, fetchRoundOff, fetchRevenue, fetchFinancials]);

  // Auto-dismiss messages
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // ============================================================
  // RENDER HELPERS
  // ============================================================

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatPercent = (value) => {
    return `${parseFloat(value || 0).toFixed(2)}%`;
  };

  // Pagination Component
  const renderPagination = () => {
    if (!pagination.pages || pagination.pages <= 1) return null;
    return (
      <div className="ca-pagination">
        <button
          disabled={pagination.page === 1}
          onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
        >
          Previous
        </button>
        <span>Page {pagination.page} of {pagination.pages}</span>
        <button
          disabled={pagination.page === pagination.pages}
          onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
        >
          Next
        </button>
      </div>
    );
  };

  // ============================================================
  // TAB CONTENT RENDERERS
  // ============================================================

  // Dashboard Tab
  const renderDashboard = () => (
    <div className="ca-dashboard">
      <div className="ca-dashboard-grid">
        <div className="ca-dashboard-card">
          <h3>GST Summary</h3>
          {dashboardData?.gstSummary ? (
            <>
              <div className="ca-stat">
                <span className="ca-stat-label">Total Outward Supplies</span>
                <span className="ca-stat-value">{formatCurrency(dashboardData.gstSummary.totalOutwardSupplies)}</span>
              </div>
              <div className="ca-stat">
                <span className="ca-stat-label">Total GST</span>
                <span className="ca-stat-value">{formatCurrency(dashboardData.gstSummary.totalGst)}</span>
              </div>
              <div className="ca-stat-row">
                <span>B2B: {dashboardData.gstSummary.b2bCount}</span>
                <span>B2C: {dashboardData.gstSummary.b2cCount}</span>
              </div>
            </>
          ) : <p className="ca-no-data">No data available</p>}
        </div>

        <div className="ca-dashboard-card">
          <h3>TDS Summary (194-O)</h3>
          {dashboardData?.tdsSummary ? (
            <>
              <div className="ca-stat">
                <span className="ca-stat-label">Total TDS Deducted</span>
                <span className="ca-stat-value">{formatCurrency(dashboardData.tdsSummary.totalTdsDeducted)}</span>
              </div>
              <div className="ca-stat">
                <span className="ca-stat-label">Total Payouts</span>
                <span className="ca-stat-value">{dashboardData.tdsSummary.totalPayouts}</span>
              </div>
              <div className="ca-stat">
                <span className="ca-stat-label">Financial Year</span>
                <span className="ca-stat-value">{dashboardData.tdsSummary.financialYear}</span>
              </div>
            </>
          ) : <p className="ca-no-data">No data available</p>}
        </div>

        <div className="ca-dashboard-card">
          <h3>Settlement Status</h3>
          {dashboardData?.settlementSummary ? (
            <>
              <div className="ca-stat">
                <span className="ca-stat-label">Total Orders</span>
                <span className="ca-stat-value">{dashboardData.settlementSummary.totalOrders}</span>
              </div>
              <div className="ca-stat">
                <span className="ca-stat-label">Match Rate</span>
                <span className="ca-stat-value">{formatPercent(dashboardData.settlementSummary.matchRate)}</span>
              </div>
              <div className="ca-stat">
                <span className="ca-stat-label">Pending Orders</span>
                <span className="ca-stat-value ca-warning">{dashboardData.settlementSummary.pendingOrders}</span>
              </div>
            </>
          ) : <p className="ca-no-data">No data available</p>}
        </div>

        <div className="ca-dashboard-card">
          <h3>Trial Balance</h3>
          {dashboardData?.trialBalanceSummary ? (
            <>
              <div className="ca-stat">
                <span className="ca-stat-label">Status</span>
                <span className={`ca-stat-value ${dashboardData.trialBalanceSummary.isTallied ? 'ca-success' : 'ca-error'}`}>
                  {dashboardData.trialBalanceSummary.isTallied ? 'Tallied' : 'Discrepancy'}
                </span>
              </div>
              <div className="ca-stat">
                <span className="ca-stat-label">Total Debits</span>
                <span className="ca-stat-value">{formatCurrency(dashboardData.trialBalanceSummary.totalDebits)}</span>
              </div>
              <div className="ca-stat">
                <span className="ca-stat-label">Total Credits</span>
                <span className="ca-stat-value">{formatCurrency(dashboardData.trialBalanceSummary.totalCredits)}</span>
              </div>
            </>
          ) : <p className="ca-no-data">No data available</p>}
        </div>
      </div>

      <div className="ca-actions">
        <button className="ca-btn ca-btn-primary" onClick={handleExportWorkbook}>
          Export Complete Excel Workbook
        </button>
      </div>
    </div>
  );

  // GSTR-1 Tab
  const renderGSTR1 = () => (
    <div className="ca-report">
      <div className="ca-sub-tabs">
        <button className={gstr1View === 'summary' ? 'active' : ''} onClick={() => setGstr1View('summary')}>Summary</button>
        <button className={gstr1View === 'b2b' ? 'active' : ''} onClick={() => setGstr1View('b2b')}>B2B Supplies</button>
        <button className={gstr1View === 'b2c' ? 'active' : ''} onClick={() => setGstr1View('b2c')}>B2C Supplies</button>
        <button className={gstr1View === 'stateWise' ? 'active' : ''} onClick={() => setGstr1View('stateWise')}>State-wise</button>
      </div>

      {gstr1Data && gstr1View === 'summary' && (
        <div className="ca-gstr1-summary">
          <div className="ca-summary-header">
            <h3>GSTR-1 Summary</h3>
            <div className="ca-meta">
              <span>GSTIN: {gstr1Data.gstin}</span>
              <span>Period: {gstr1Data.filingPeriod}</span>
              <span>FY: {gstr1Data.financialYear}</span>
            </div>
          </div>
          <div className="ca-summary-grid">
            <div className="ca-summary-item">
              <span className="ca-label">Total Outward Supplies</span>
              <span className="ca-value">{formatCurrency(gstr1Data.summary?.totalOutwardSupplies)}</span>
            </div>
            <div className="ca-summary-item">
              <span className="ca-label">Total Taxable Value</span>
              <span className="ca-value">{formatCurrency(gstr1Data.summary?.totalTaxableValue)}</span>
            </div>
            <div className="ca-summary-item">
              <span className="ca-label">CGST</span>
              <span className="ca-value">{formatCurrency(gstr1Data.summary?.totalCGST)}</span>
            </div>
            <div className="ca-summary-item">
              <span className="ca-label">SGST</span>
              <span className="ca-value">{formatCurrency(gstr1Data.summary?.totalSGST)}</span>
            </div>
            <div className="ca-summary-item">
              <span className="ca-label">IGST</span>
              <span className="ca-value">{formatCurrency(gstr1Data.summary?.totalIGST)}</span>
            </div>
            <div className="ca-summary-item">
              <span className="ca-label">Total Invoices</span>
              <span className="ca-value">{gstr1Data.summary?.totalInvoices}</span>
            </div>
          </div>
        </div>
      )}

      {gstr1Data && gstr1View === 'b2b' && (
        <div className="ca-table-container">
          <table className="ca-table">
            <thead>
              <tr>
                <th>Invoice No.</th>
                <th>Invoice Date</th>
                <th>GSTIN</th>
                <th>Recipient</th>
                <th>Taxable Value</th>
                <th>CGST</th>
                <th>SGST</th>
                <th>IGST</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {gstr1Data.b2b?.map((inv, idx) => (
                <tr key={idx}>
                  <td>{inv.invoiceNumber}</td>
                  <td>{formatDate(inv.invoiceDate)}</td>
                  <td className="ca-mono">{inv.recipientGstin}</td>
                  <td>{inv.recipientName}</td>
                  <td className="ca-right">{formatCurrency(inv.taxableValue)}</td>
                  <td className="ca-right">{formatCurrency(inv.cgst)}</td>
                  <td className="ca-right">{formatCurrency(inv.sgst)}</td>
                  <td className="ca-right">{formatCurrency(inv.igst)}</td>
                  <td className="ca-right">{formatCurrency(inv.totalValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {renderPagination()}
        </div>
      )}

      {gstr1Data && gstr1View === 'b2c' && (
        <div className="ca-table-container">
          <table className="ca-table">
            <thead>
              <tr>
                <th>State</th>
                <th>State Code</th>
                <th>Taxable Value</th>
                <th>CGST</th>
                <th>SGST</th>
                <th>Total Value</th>
              </tr>
            </thead>
            <tbody>
              {gstr1Data.b2c?.map((state, idx) => (
                <tr key={idx}>
                  <td>{state.stateName}</td>
                  <td className="ca-mono">{state.stateCode}</td>
                  <td className="ca-right">{formatCurrency(state.totalTaxableValue)}</td>
                  <td className="ca-right">{formatCurrency(state.totalCGST)}</td>
                  <td className="ca-right">{formatCurrency(state.totalSGST)}</td>
                  <td className="ca-right">{formatCurrency(state.totalValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {gstr1Data && gstr1View === 'stateWise' && (
        <div className="ca-table-container">
          <table className="ca-table">
            <thead>
              <tr>
                <th>State</th>
                <th>State Code</th>
                <th>Type</th>
                <th>CGST</th>
                <th>SGST</th>
                <th>IGST</th>
                <th>Total GST</th>
              </tr>
            </thead>
            <tbody>
              {gstr1Data.stateWise?.map((state, idx) => (
                <tr key={idx}>
                  <td>{state.stateName}</td>
                  <td className="ca-mono">{state.stateCode}</td>
                  <td><span className={`ca-badge ${state.gstType}`}>{state.gstType}</span></td>
                  <td className="ca-right">{formatCurrency(state.cgst)}</td>
                  <td className="ca-right">{formatCurrency(state.sgst)}</td>
                  <td className="ca-right">{formatCurrency(state.igst)}</td>
                  <td className="ca-right">{formatCurrency(state.totalGst)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // GSTR-3B Tab
  const renderGSTR3B = () => (
    <div className="ca-report">
      {gstr3bData && (
        <div className="ca-gstr3b">
          <div className="ca-summary-header">
            <h3>GSTR-3B Summary</h3>
            <div className="ca-meta">
              <span>GSTIN: {gstr3bData.gstin}</span>
              <span>Period: {gstr3bData.filingPeriod}</span>
            </div>
          </div>

          <div className="ca-section">
            <h4>3.1 - Details of Outward Supplies</h4>
            <div className="ca-table-container">
              <table className="ca-table">
                <thead>
                  <tr>
                    <th>Nature of Supplies</th>
                    <th>Taxable Value</th>
                    <th>IGST</th>
                    <th>CGST</th>
                    <th>SGST</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Outward Taxable Supplies</td>
                    <td className="ca-right">{formatCurrency(gstr3bData.section3_1?.outwardTaxable?.taxableValue)}</td>
                    <td className="ca-right">{formatCurrency(gstr3bData.section3_1?.outwardTaxable?.igst)}</td>
                    <td className="ca-right">{formatCurrency(gstr3bData.section3_1?.outwardTaxable?.cgst)}</td>
                    <td className="ca-right">{formatCurrency(gstr3bData.section3_1?.outwardTaxable?.sgst)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="ca-section">
            <h4>6.1 - Tax Payable</h4>
            <div className="ca-summary-grid">
              <div className="ca-summary-item">
                <span className="ca-label">IGST Payable</span>
                <span className="ca-value">{formatCurrency(gstr3bData.section6?.taxPayable?.igst)}</span>
              </div>
              <div className="ca-summary-item">
                <span className="ca-label">CGST Payable</span>
                <span className="ca-value">{formatCurrency(gstr3bData.section6?.taxPayable?.cgst)}</span>
              </div>
              <div className="ca-summary-item">
                <span className="ca-label">SGST Payable</span>
                <span className="ca-value">{formatCurrency(gstr3bData.section6?.taxPayable?.sgst)}</span>
              </div>
              <div className="ca-summary-item ca-highlight">
                <span className="ca-label">Total Tax Payable</span>
                <span className="ca-value">{formatCurrency(gstr3bData.section6?.taxPayable?.totalPayable)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // TDS Tab
  const renderTDS = () => (
    <div className="ca-report">
      <div className="ca-sub-tabs">
        <button className={tdsView === 'register' ? 'active' : ''} onClick={() => setTdsView('register')}>TDS Register</button>
        <button className={tdsView === 'deductees' ? 'active' : ''} onClick={() => setTdsView('deductees')}>Deductees Summary</button>
        <button className={tdsView === 'quarterly' ? 'active' : ''} onClick={() => setTdsView('quarterly')}>Quarterly Summary</button>
        <button className={tdsView === 'certificates' ? 'active' : ''} onClick={() => setTdsView('certificates')}>Certificates</button>
      </div>

      {tdsView === 'quarterly' && (
        <div className="ca-filter-row">
          <label>
            Financial Year:
            <select value={tdsYear} onChange={(e) => setTdsYear(parseInt(e.target.value))}>
              {[0, 1, 2, 3].map(offset => {
                const year = getCurrentFinancialYear() - offset;
                return <option key={year} value={year}>{year}-{(year + 1).toString().slice(-2)}</option>;
              })}
            </select>
          </label>
        </div>
      )}

      {tdsData && tdsView === 'register' && (
        <div className="ca-table-container">
          <div className="ca-totals-bar">
            <span>Total Gross: {formatCurrency(tdsData.totals?.totalGrossValue)}</span>
            <span>Total TDS: {formatCurrency(tdsData.totals?.totalTdsDeducted)}</span>
            <span>Total Net Paid: {formatCurrency(tdsData.totals?.totalNetPaid)}</span>
            <span>Section: {tdsData.section}</span>
            <span>Rate: {tdsData.tdsRate}%</span>
          </div>
          <table className="ca-table">
            <thead>
              <tr>
                <th>Payout ID</th>
                <th>Date</th>
                <th>Deductee Name</th>
                <th>PAN</th>
                <th>Section</th>
                <th>Gross Value</th>
                <th>TDS</th>
                <th>Net Paid</th>
                <th>Bank Ref</th>
              </tr>
            </thead>
            <tbody>
              {tdsData.register?.map((entry, idx) => (
                <tr key={idx}>
                  <td className="ca-mono">{entry.payoutId}</td>
                  <td>{formatDate(entry.payoutDate)}</td>
                  <td>{entry.deducteeName}</td>
                  <td className="ca-mono">{entry.deducteePAN}</td>
                  <td>{entry.section}</td>
                  <td className="ca-right">{formatCurrency(entry.grossValue)}</td>
                  <td className="ca-right">{formatCurrency(entry.tdsAmount)}</td>
                  <td className="ca-right">{formatCurrency(entry.netPaid)}</td>
                  <td className="ca-mono">{entry.bankReference}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {renderPagination()}
        </div>
      )}

      {tdsData && tdsView === 'deductees' && (
        <div className="ca-table-container">
          <table className="ca-table">
            <thead>
              <tr>
                <th>Restaurant Name</th>
                <th>PAN</th>
                <th>Total Gross</th>
                <th>Total TDS</th>
                <th>Total Net Paid</th>
                <th>Payouts</th>
                <th>Certificate</th>
              </tr>
            </thead>
            <tbody>
              {tdsData.deductees?.map((d, idx) => (
                <tr key={idx}>
                  <td>{d.restaurantName}</td>
                  <td className="ca-mono">{d.pan}</td>
                  <td className="ca-right">{formatCurrency(d.totalGrossValue)}</td>
                  <td className="ca-right">{formatCurrency(d.totalTdsDeducted)}</td>
                  <td className="ca-right">{formatCurrency(d.totalNetPaid)}</td>
                  <td className="ca-center">{d.payoutCount}</td>
                  <td className="ca-center">
                    <span className={`ca-badge ${d.certificateIssued ? 'success' : 'pending'}`}>
                      {d.certificateIssued ? 'Issued' : 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {renderPagination()}
        </div>
      )}

      {tdsData && tdsView === 'quarterly' && (
        <div className="ca-quarterly">
          <h3>Quarterly TDS Summary - FY {tdsData.financialYear}</h3>
          <div className="ca-table-container">
            <table className="ca-table">
              <thead>
                <tr>
                  <th>Quarter</th>
                  <th>Period</th>
                  <th>Gross Value</th>
                  <th>TDS Deducted</th>
                  <th>Payouts</th>
                  <th>Deductees</th>
                </tr>
              </thead>
              <tbody>
                {tdsData.quarters?.map((q, idx) => (
                  <tr key={idx}>
                    <td>{q.quarter}</td>
                    <td>{formatDate(q.periodStart)} - {formatDate(q.periodEnd)}</td>
                    <td className="ca-right">{formatCurrency(q.totalGrossValue)}</td>
                    <td className="ca-right">{formatCurrency(q.totalTdsDeducted)}</td>
                    <td className="ca-center">{q.payoutCount}</td>
                    <td className="ca-center">{q.deducteeCount}</td>
                  </tr>
                ))}
                <tr className="ca-total-row">
                  <td colSpan="2"><strong>Year Total</strong></td>
                  <td className="ca-right"><strong>{formatCurrency(tdsData.yearTotals?.totalGrossValue)}</strong></td>
                  <td className="ca-right"><strong>{formatCurrency(tdsData.yearTotals?.totalTdsDeducted)}</strong></td>
                  <td className="ca-center"><strong>{tdsData.yearTotals?.payoutCount}</strong></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tdsData && tdsView === 'certificates' && (
        <div className="ca-table-container">
          <div className="ca-totals-bar">
            <span>Total: {tdsData.summary?.total}</span>
            <span>Issued: {tdsData.summary?.issued}</span>
            <span>Pending: {tdsData.summary?.pending}</span>
          </div>
          <table className="ca-table">
            <thead>
              <tr>
                <th>Restaurant Name</th>
                <th>PAN</th>
                <th>Total TDS</th>
                <th>Status</th>
                <th>Certificate No.</th>
                <th>Certificate Date</th>
              </tr>
            </thead>
            <tbody>
              {tdsData.certificates?.map((c, idx) => (
                <tr key={idx}>
                  <td>{c.restaurantName}</td>
                  <td className="ca-mono">{c.pan}</td>
                  <td className="ca-right">{formatCurrency(c.totalTdsDeducted)}</td>
                  <td>
                    <span className={`ca-badge ${c.certificateStatus}`}>
                      {c.certificateStatus}
                    </span>
                  </td>
                  <td className="ca-mono">{c.certificateNumber || '-'}</td>
                  <td>{formatDate(c.certificateDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // HSN/SAC Tab
  const renderHSNSAC = () => (
    <div className="ca-report">
      <div className="ca-sub-tabs">
        <button className={hsnSacView === 'summary' ? 'active' : ''} onClick={() => setHsnSacView('summary')}>Summary</button>
        <button className={hsnSacView === 'rateWise' ? 'active' : ''} onClick={() => setHsnSacView('rateWise')}>Rate-wise</button>
      </div>

      {hsnSacData && hsnSacView === 'summary' && (
        <div className="ca-table-container">
          <div className="ca-totals-bar">
            <span>Total Taxable: {formatCurrency(hsnSacData.totals?.totalTaxableValue)}</span>
            <span>Total GST: {formatCurrency(hsnSacData.totals?.totalGST)}</span>
            <span>Total Value: {formatCurrency(hsnSacData.totals?.totalValue)}</span>
          </div>
          <table className="ca-table">
            <thead>
              <tr>
                <th>HSN/SAC Code</th>
                <th>Description</th>
                <th>Qty</th>
                <th>Taxable Value</th>
                <th>CGST</th>
                <th>SGST</th>
                <th>IGST</th>
                <th>Total GST</th>
                <th>Total Value</th>
              </tr>
            </thead>
            <tbody>
              {hsnSacData.summary?.map((item, idx) => (
                <tr key={idx}>
                  <td className="ca-mono">{item.hsnSac}</td>
                  <td>{item.description}</td>
                  <td className="ca-center">{item.totalQuantity}</td>
                  <td className="ca-right">{formatCurrency(item.totalTaxableValue)}</td>
                  <td className="ca-right">{formatCurrency(item.totalCGST)}</td>
                  <td className="ca-right">{formatCurrency(item.totalSGST)}</td>
                  <td className="ca-right">{formatCurrency(item.totalIGST)}</td>
                  <td className="ca-right">{formatCurrency(item.totalGST)}</td>
                  <td className="ca-right">{formatCurrency(item.totalValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {renderPagination()}
        </div>
      )}

      {hsnSacData && hsnSacView === 'rateWise' && (
        <div className="ca-rate-wise">
          {hsnSacData.rateWise?.map((rateGroup, idx) => (
            <div key={idx} className="ca-rate-group">
              <h4>GST Rate: {rateGroup.gstRate}%</h4>
              <div className="ca-table-container">
                <table className="ca-table">
                  <thead>
                    <tr>
                      <th>HSN/SAC</th>
                      <th>Description</th>
                      <th>Taxable Value</th>
                      <th>CGST</th>
                      <th>SGST</th>
                      <th>IGST</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rateGroup.items.map((item, i) => (
                      <tr key={i}>
                        <td className="ca-mono">{item.hsnSac}</td>
                        <td>{item.description}</td>
                        <td className="ca-right">{formatCurrency(item.totalTaxableValue)}</td>
                        <td className="ca-right">{formatCurrency(item.totalCGST)}</td>
                        <td className="ca-right">{formatCurrency(item.totalSGST)}</td>
                        <td className="ca-right">{formatCurrency(item.totalIGST)}</td>
                        <td className="ca-right">{formatCurrency(item.totalValue)}</td>
                      </tr>
                    ))}
                    <tr className="ca-total-row">
                      <td colSpan="2"><strong>Subtotal ({rateGroup.gstRate}%)</strong></td>
                      <td className="ca-right"><strong>{formatCurrency(rateGroup.totalTaxableValue)}</strong></td>
                      <td className="ca-right"><strong>{formatCurrency(rateGroup.totalCGST)}</strong></td>
                      <td className="ca-right"><strong>{formatCurrency(rateGroup.totalSGST)}</strong></td>
                      <td className="ca-right"><strong>{formatCurrency(rateGroup.totalIGST)}</strong></td>
                      <td className="ca-right"><strong>{formatCurrency(rateGroup.totalValue)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Registers Tab
  const renderRegisters = () => (
    <div className="ca-report">
      <div className="ca-sub-tabs">
        <button className={registerType === 'sales' ? 'active' : ''} onClick={() => setRegisterType('sales')}>Sales Register</button>
        <button className={registerType === 'settlements' ? 'active' : ''} onClick={() => setRegisterType('settlements')}>Settlement Register</button>
        <button className={registerType === 'creditNotes' ? 'active' : ''} onClick={() => setRegisterType('creditNotes')}>Credit Notes</button>
        <button className={registerType === 'debitNotes' ? 'active' : ''} onClick={() => setRegisterType('debitNotes')}>Debit Notes</button>
        <button className={registerType === 'dpEarnings' ? 'active' : ''} onClick={() => setRegisterType('dpEarnings')}>DP Earnings</button>
      </div>

      {registerData && (
        <div className="ca-table-container">
          <table className="ca-table">
            <thead>
              <tr>
                {registerType === 'sales' && (
                  <>
                    <th>Invoice No.</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Order</th>
                    <th>Recipient</th>
                    <th>Taxable</th>
                    <th>Tax</th>
                    <th>Total</th>
                  </>
                )}
                {registerType === 'settlements' && (
                  <>
                    <th>Invoice No.</th>
                    <th>Date</th>
                    <th>Restaurant</th>
                    <th>Payout ID</th>
                    <th>Period</th>
                    <th>Orders</th>
                    <th>Gross</th>
                    <th>Deductions</th>
                    <th>Net</th>
                    <th>Status</th>
                  </>
                )}
                {(registerType === 'creditNotes' || registerType === 'debitNotes') && (
                  <>
                    <th>Invoice No.</th>
                    <th>Date</th>
                    <th>Order</th>
                    <th>Recipient</th>
                    <th>Reason</th>
                    <th>Original Invoice</th>
                    <th>Taxable</th>
                    <th>Tax</th>
                    <th>Total</th>
                  </>
                )}
                {registerType === 'dpEarnings' && (
                  <>
                    <th>Payout ID</th>
                    <th>Date</th>
                    <th>Partner Name</th>
                    <th>Phone</th>
                    <th>Orders</th>
                    <th>Gross</th>
                    <th>Deductions</th>
                    <th>Net</th>
                    <th>UTR</th>
                    <th>Status</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {registerData.register?.map((entry, idx) => (
                <tr key={idx}>
                  {registerType === 'sales' && (
                    <>
                      <td className="ca-mono">{entry.invoiceNumber}</td>
                      <td>{formatDate(entry.invoiceDate)}</td>
                      <td>{entry.invoiceType}</td>
                      <td>{entry.orderNumber}</td>
                      <td>{entry.recipientName}</td>
                      <td className="ca-right">{formatCurrency(entry.taxableValue)}</td>
                      <td className="ca-right">{formatCurrency(entry.totalTax)}</td>
                      <td className="ca-right">{formatCurrency(entry.grandTotal)}</td>
                    </>
                  )}
                  {registerType === 'settlements' && (
                    <>
                      <td className="ca-mono">{entry.invoiceNumber}</td>
                      <td>{formatDate(entry.invoiceDate)}</td>
                      <td>{entry.restaurantName}</td>
                      <td className="ca-mono">{entry.payoutId}</td>
                      <td>{formatDate(entry.periodStart)} - {formatDate(entry.periodEnd)}</td>
                      <td className="ca-center">{entry.totalOrders}</td>
                      <td className="ca-right">{formatCurrency(entry.grossOrderValue)}</td>
                      <td className="ca-right">{formatCurrency(entry.totalDeductions)}</td>
                      <td className="ca-right">{formatCurrency(entry.netPayable)}</td>
                      <td><span className={`ca-badge ${entry.payoutStatus}`}>{entry.payoutStatus}</span></td>
                    </>
                  )}
                  {(registerType === 'creditNotes' || registerType === 'debitNotes') && (
                    <>
                      <td className="ca-mono">{entry.invoiceNumber}</td>
                      <td>{formatDate(entry.invoiceDate)}</td>
                      <td>{entry.orderNumber}</td>
                      <td>{entry.recipientName}</td>
                      <td>{entry.reason}</td>
                      <td className="ca-mono">{entry.originalInvoice}</td>
                      <td className="ca-right">{formatCurrency(entry.taxableValue)}</td>
                      <td className="ca-right">{formatCurrency(entry.totalTax)}</td>
                      <td className="ca-right">{formatCurrency(entry.grandTotal)}</td>
                    </>
                  )}
                  {registerType === 'dpEarnings' && (
                    <>
                      <td className="ca-mono">{entry.payoutId}</td>
                      <td>{formatDate(entry.payoutDate)}</td>
                      <td>{entry.partnerName}</td>
                      <td>{entry.partnerPhone}</td>
                      <td className="ca-center">{entry.orderCount}</td>
                      <td className="ca-right">{formatCurrency(entry.grossEarnings)}</td>
                      <td className="ca-right">{formatCurrency(entry.deductions)}</td>
                      <td className="ca-right">{formatCurrency(entry.netAmount)}</td>
                      <td className="ca-mono">{entry.utr}</td>
                      <td><span className={`ca-badge ${entry.status}`}>{entry.status}</span></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {renderPagination()}
        </div>
      )}
    </div>
  );

  // Settlement Reconciliation Tab
  const renderSettlement = () => (
    <div className="ca-report">
      <div className="ca-sub-tabs">
        <button className={settlementView === 'reconciliation' ? 'active' : ''} onClick={() => setSettlementView('reconciliation')}>Reconciliation</button>
        <button className={settlementView === 'discrepancies' ? 'active' : ''} onClick={() => setSettlementView('discrepancies')}>Discrepancies</button>
        <button className={settlementView === 'bankTracking' ? 'active' : ''} onClick={() => setSettlementView('bankTracking')}>Bank Tracking</button>
      </div>

      {settlementData && (settlementView === 'reconciliation' || settlementView === 'discrepancies') && (
        <div className="ca-table-container">
          <div className="ca-totals-bar">
            <span>Total Orders: {settlementData.summary?.totalOrders}</span>
            <span>Match Rate: {settlementData.summary?.matchRate}%</span>
            <span>Paid: {settlementData.summary?.paidOrders}</span>
            <span>Pending: {settlementData.summary?.pendingOrders}</span>
          </div>
          <table className="ca-table">
            <thead>
              <tr>
                <th>Order No.</th>
                <th>Restaurant</th>
                <th>Delivered</th>
                <th>Expected Gross</th>
                <th>Expected Net</th>
                <th>Actual Paid</th>
                <th>Variance</th>
                <th>Status</th>
                <th>UTR</th>
              </tr>
            </thead>
            <tbody>
              {settlementData.reconciliation?.map((entry, idx) => (
                <tr key={idx} className={entry.reconciliationStatus === 'discrepancy' ? 'ca-row-warning' : ''}>
                  <td className="ca-mono">{entry.orderNumber}</td>
                  <td>{entry.restaurantName}</td>
                  <td>{formatDate(entry.deliveredAt)}</td>
                  <td className="ca-right">{formatCurrency(entry.expectedGross)}</td>
                  <td className="ca-right">{formatCurrency(entry.expectedNet)}</td>
                  <td className="ca-right">{formatCurrency(entry.actualPaidAmount)}</td>
                  <td className={`ca-right ${entry.variance !== 0 ? 'ca-warning' : ''}`}>
                    {formatCurrency(entry.variance)}
                  </td>
                  <td>
                    <span className={`ca-badge ${entry.reconciliationStatus}`}>
                      {entry.reconciliationStatus}
                    </span>
                  </td>
                  <td className="ca-mono">{entry.utr || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {renderPagination()}
        </div>
      )}

      {settlementData && settlementView === 'bankTracking' && (
        <div className="ca-table-container">
          <div className="ca-status-summary">
            {Object.entries(settlementData.statusSummary || {}).map(([status, data]) => (
              <div key={status} className="ca-status-item">
                <span className={`ca-badge ${status}`}>{status}</span>
                <span>{data.count} payouts</span>
                <span>{formatCurrency(data.totalAmount)}</span>
              </div>
            ))}
          </div>
          <table className="ca-table">
            <thead>
              <tr>
                <th>Payout ID</th>
                <th>Recipient</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Mode</th>
                <th>Bank Account</th>
                <th>UTR</th>
                <th>Created</th>
                <th>Completed</th>
              </tr>
            </thead>
            <tbody>
              {settlementData.tracking?.map((entry, idx) => (
                <tr key={idx}>
                  <td className="ca-mono">{entry.payoutId}</td>
                  <td>{entry.recipientName}</td>
                  <td>{entry.recipientType}</td>
                  <td className="ca-right">{formatCurrency(entry.amount)}</td>
                  <td><span className={`ca-badge ${entry.status}`}>{entry.status}</span></td>
                  <td>{entry.paymentMode}</td>
                  <td className="ca-mono">{entry.bankAccount} ({entry.ifsc})</td>
                  <td className="ca-mono">{entry.utr || '-'}</td>
                  <td>{formatDate(entry.createdAt)}</td>
                  <td>{formatDate(entry.completedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {renderPagination()}
        </div>
      )}
    </div>
  );

  // Round-off Tab
  const renderRoundOff = () => (
    <div className="ca-report">
      <div className="ca-sub-tabs">
        <button className={roundOffView === 'report' ? 'active' : ''} onClick={() => setRoundOffView('report')}>Detailed Report</button>
        <button className={roundOffView === 'summary' ? 'active' : ''} onClick={() => setRoundOffView('summary')}>Summary</button>
      </div>

      {roundOffView === 'summary' && (
        <div className="ca-filter-row">
          <label>
            Group By:
            <select value={roundOffGroupBy} onChange={(e) => setRoundOffGroupBy(e.target.value)}>
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </label>
        </div>
      )}

      {roundOffData && roundOffView === 'report' && (
        <div className="ca-table-container">
          <div className="ca-totals-bar">
            <span>Total Round Up: {formatCurrency(roundOffData.totals?.totalRoundUp)}</span>
            <span>Total Round Down: {formatCurrency(roundOffData.totals?.totalRoundDown)}</span>
            <span className={roundOffData.totals?.netRoundOff >= 0 ? 'ca-success' : 'ca-warning'}>
              Net: {formatCurrency(roundOffData.totals?.netRoundOff)}
            </span>
            <span>Orders: {roundOffData.totals?.orderCount}</span>
          </div>
          <table className="ca-table">
            <thead>
              <tr>
                <th>Order No.</th>
                <th>Restaurant</th>
                <th>Date</th>
                <th>Exact Total</th>
                <th>Round Off</th>
                <th>Grand Total</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {roundOffData.entries?.map((entry, idx) => (
                <tr key={idx}>
                  <td className="ca-mono">{entry.orderNumber}</td>
                  <td>{entry.restaurantName}</td>
                  <td>{formatDate(entry.orderDate)}</td>
                  <td className="ca-right">{formatCurrency(entry.exactTotal)}</td>
                  <td className={`ca-right ${entry.roundOff >= 0 ? 'ca-success' : 'ca-warning'}`}>
                    {entry.roundOff >= 0 ? '+' : ''}{formatCurrency(entry.roundOff)}
                  </td>
                  <td className="ca-right">{formatCurrency(entry.grandTotal)}</td>
                  <td><span className={`ca-badge ${entry.roundOffType.toLowerCase()}`}>{entry.roundOffType}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {renderPagination()}
        </div>
      )}

      {roundOffData && roundOffView === 'summary' && (
        <div className="ca-table-container">
          <table className="ca-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Total Orders</th>
                <th>Orders with Round-off</th>
                <th>Round Up</th>
                <th>Round Down</th>
                <th>Net Round-off</th>
              </tr>
            </thead>
            <tbody>
              {roundOffData.summary?.map((entry, idx) => (
                <tr key={idx}>
                  <td>{entry.period}</td>
                  <td className="ca-center">{entry.totalOrders}</td>
                  <td className="ca-center">{entry.ordersWithRoundOff}</td>
                  <td className="ca-right ca-success">+{formatCurrency(entry.totalRoundUp)}</td>
                  <td className="ca-right ca-warning">-{formatCurrency(entry.totalRoundDown)}</td>
                  <td className={`ca-right ${entry.netRoundOff >= 0 ? 'ca-success' : 'ca-warning'}`}>
                    {formatCurrency(entry.netRoundOff)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Revenue Recognition Tab
  const renderRevenue = () => (
    <div className="ca-report">
      <div className="ca-sub-tabs">
        <button className={revenueView === 'recognition' ? 'active' : ''} onClick={() => setRevenueView('recognition')}>Revenue Recognition</button>
        <button className={revenueView === 'cutoff' ? 'active' : ''} onClick={() => setRevenueView('cutoff')}>Revenue Cutoff</button>
        <button className={revenueView === 'deferred' ? 'active' : ''} onClick={() => setRevenueView('deferred')}>Deferred Revenue</button>
      </div>

      {revenueView === 'recognition' && (
        <div className="ca-filter-row">
          <label>
            Group By:
            <select value={revenueGroupBy} onChange={(e) => setRevenueGroupBy(e.target.value)}>
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </label>
        </div>
      )}

      {revenueData && revenueView === 'recognition' && (
        <div className="ca-table-container">
          <div className="ca-totals-bar">
            <span>Total Gross: {formatCurrency(revenueData.totals?.grossOrderValue)}</span>
            <span>Platform Earnings: {formatCurrency(revenueData.totals?.platformEarnings)}</span>
            <span>Net Platform: {formatCurrency(revenueData.totals?.netPlatformEarnings)}</span>
          </div>
          <table className="ca-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Orders</th>
                <th>Gross Order Value</th>
                <th>Food Revenue</th>
                <th>Delivery Fee</th>
                <th>Platform Fee</th>
                <th>GST Collected</th>
                <th>Platform Earnings</th>
              </tr>
            </thead>
            <tbody>
              {revenueData.revenue?.map((entry, idx) => (
                <tr key={idx}>
                  <td>{entry._id}</td>
                  <td className="ca-center">{entry.orderCount}</td>
                  <td className="ca-right">{formatCurrency(entry.grossOrderValue)}</td>
                  <td className="ca-right">{formatCurrency(entry.foodRevenue)}</td>
                  <td className="ca-right">{formatCurrency(entry.deliveryFeeRevenue)}</td>
                  <td className="ca-right">{formatCurrency(entry.platformFeeRevenue)}</td>
                  <td className="ca-right">{formatCurrency(entry.gstCollected)}</td>
                  <td className="ca-right">{formatCurrency(entry.netPlatformEarnings)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {revenueData && revenueView === 'cutoff' && (
        <div className="ca-cutoff">
          <h3>Revenue Cutoff Analysis - As of {formatDate(revenueData.asOfDate)}</h3>

          <div className="ca-section">
            <h4>Delivered Orders - Unpaid</h4>
            <div className="ca-summary-grid">
              <div className="ca-summary-item">
                <span className="ca-label">Order Count</span>
                <span className="ca-value">{revenueData.deliveredUnpaid?.orderCount}</span>
              </div>
              <div className="ca-summary-item">
                <span className="ca-label">Restaurant Earnings Pending</span>
                <span className="ca-value">{formatCurrency(revenueData.deliveredUnpaid?.totalRestaurantEarnings)}</span>
              </div>
              <div className="ca-summary-item">
                <span className="ca-label">Delivery Earnings Pending</span>
                <span className="ca-value">{formatCurrency(revenueData.deliveredUnpaid?.totalDeliveryEarnings)}</span>
              </div>
              <div className="ca-summary-item">
                <span className="ca-label">Platform Earnings Pending</span>
                <span className="ca-value">{formatCurrency(revenueData.deliveredUnpaid?.totalPlatformEarnings)}</span>
              </div>
            </div>
          </div>

          <div className="ca-section">
            <h4>Orders In Progress</h4>
            <div className="ca-table-container">
              <table className="ca-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Count</th>
                    <th>Total Value</th>
                  </tr>
                </thead>
                <tbody>
                  {revenueData.ordersInProgress?.map((entry, idx) => (
                    <tr key={idx}>
                      <td><span className="ca-badge">{entry.status}</span></td>
                      <td className="ca-center">{entry.count}</td>
                      <td className="ca-right">{formatCurrency(entry.totalValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {revenueData && revenueView === 'deferred' && (
        <div className="ca-deferred">
          <h3>Deferred Revenue - As of {formatDate(revenueData.asOfDate)}</h3>
          <div className="ca-summary-item ca-highlight">
            <span className="ca-label">Total Deferred Revenue</span>
            <span className="ca-value">{formatCurrency(revenueData.totalDeferred)}</span>
          </div>

          <div className="ca-table-container">
            <table className="ca-table">
              <thead>
                <tr>
                  <th>Restaurant</th>
                  <th>Plan</th>
                  <th>Period Start</th>
                  <th>Next Billing</th>
                  <th>Days Remaining</th>
                  <th>Deferred Amount</th>
                </tr>
              </thead>
              <tbody>
                {revenueData.subscriptions?.map((sub, idx) => (
                  <tr key={idx}>
                    <td>{sub.restaurantName}</td>
                    <td>{sub.planName}</td>
                    <td>{formatDate(sub.currentPeriodStart)}</td>
                    <td>{formatDate(sub.nextBillingDate)}</td>
                    <td className="ca-center">{sub.daysRemaining}</td>
                    <td className="ca-right">{formatCurrency(sub.deferredAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  // Trial Balance & P&L Tab
  const renderFinancials = () => (
    <div className="ca-report">
      <div className="ca-sub-tabs">
        <button className={financialView === 'trialBalance' ? 'active' : ''} onClick={() => setFinancialView('trialBalance')}>Trial Balance</button>
        <button className={financialView === 'profitLoss' ? 'active' : ''} onClick={() => setFinancialView('profitLoss')}>Profit & Loss</button>
      </div>

      {trialBalanceData && financialView === 'trialBalance' && (
        <div className="ca-trial-balance">
          <h3>Trial Balance - As of {formatDate(trialBalanceData.asOfDate)}</h3>
          <div className={`ca-tb-status ${trialBalanceData.totals?.isTallied ? 'ca-success' : 'ca-error'}`}>
            {trialBalanceData.totals?.isTallied ? '✓ Trial Balance Tallied' : '✗ Discrepancy Detected'}
          </div>

          <div className="ca-table-container">
            <table className="ca-table">
              <thead>
                <tr>
                  <th>Account Type</th>
                  <th>Debits</th>
                  <th>Credits</th>
                  <th>Balance</th>
                  <th>Transactions</th>
                </tr>
              </thead>
              <tbody>
                {trialBalanceData.accounts?.map((acc, idx) => (
                  <tr key={idx}>
                    <td>{acc.accountType}</td>
                    <td className="ca-right">{formatCurrency(acc.totalDebits)}</td>
                    <td className="ca-right">{formatCurrency(acc.totalCredits)}</td>
                    <td className={`ca-right ${acc.balance >= 0 ? '' : 'ca-warning'}`}>
                      {formatCurrency(acc.balance)}
                    </td>
                    <td className="ca-center">{acc.transactionCount}</td>
                  </tr>
                ))}
                <tr className="ca-total-row">
                  <td><strong>TOTALS</strong></td>
                  <td className="ca-right"><strong>{formatCurrency(trialBalanceData.totals?.totalDebits)}</strong></td>
                  <td className="ca-right"><strong>{formatCurrency(trialBalanceData.totals?.totalCredits)}</strong></td>
                  <td className={`ca-right ${trialBalanceData.totals?.difference === 0 ? 'ca-success' : 'ca-error'}`}>
                    <strong>{formatCurrency(trialBalanceData.totals?.difference)}</strong>
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {plData && financialView === 'profitLoss' && (
        <div className="ca-profit-loss">
          <h3>Profit & Loss Statement</h3>
          <p className="ca-period">Period: {formatDate(plData.period?.startDate)} to {formatDate(plData.period?.endDate)}</p>

          <div className="ca-section">
            <h4>Revenue</h4>
            <div className="ca-pl-grid">
              <div className="ca-pl-item">
                <span>Platform Fees</span>
                <span>{formatCurrency(plData.revenue?.platformFees)}</span>
              </div>
              <div className="ca-pl-item">
                <span>Restaurant Commission</span>
                <span>{formatCurrency(plData.revenue?.restaurantCommission)}</span>
              </div>
              <div className="ca-pl-item">
                <span>Delivery Commission</span>
                <span>{formatCurrency(plData.revenue?.deliveryCommission)}</span>
              </div>
              <div className="ca-pl-item">
                <span>Subscription Income</span>
                <span>{formatCurrency(plData.revenue?.subscriptionIncome)}</span>
              </div>
              <div className="ca-pl-item">
                <span>Advertising Income</span>
                <span>{formatCurrency(plData.revenue?.advertisingIncome)}</span>
              </div>
              <div className="ca-pl-item">
                <span>Payout Fees</span>
                <span>{formatCurrency(plData.revenue?.payoutFees)}</span>
              </div>
              <div className="ca-pl-item ca-total">
                <span><strong>Total Revenue</strong></span>
                <span><strong>{formatCurrency(plData.totalRevenue)}</strong></span>
              </div>
            </div>
          </div>

          <div className="ca-section">
            <h4>Expenses</h4>
            <div className="ca-pl-grid">
              <div className="ca-pl-item">
                <span>Discounts Absorbed</span>
                <span>{formatCurrency(plData.expenses?.discountsAbsorbed)}</span>
              </div>
              <div className="ca-pl-item">
                <span>GST Payable</span>
                <span>{formatCurrency(plData.expenses?.gstPayable)}</span>
              </div>
              <div className="ca-pl-item ca-total">
                <span><strong>Total Expenses</strong></span>
                <span><strong>{formatCurrency(plData.totalExpenses)}</strong></span>
              </div>
            </div>
          </div>

          <div className="ca-section ca-net-profit">
            <div className="ca-pl-item ca-highlight">
              <span><strong>Net Profit</strong></span>
              <span className={plData.netProfit >= 0 ? 'ca-success' : 'ca-error'}>
                <strong>{formatCurrency(plData.netProfit)}</strong>
              </span>
            </div>
            <div className="ca-pl-item">
              <span>Net Profit Margin</span>
              <span>{plData.netProfitMargin}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ============================================================
  // MAIN RENDER
  // ============================================================

  return (
    <div className="ca-audit-page">
      <Navbar />
      <div className="ca-audit-container">
        <header className="ca-header">
          <h1>CA Audit Reports</h1>
          <p>Comprehensive CA-grade financial audit reports for Indian legal compliance</p>
        </header>

        {/* Message Banner */}
        {message.text && (
          <div className={`ca-message ca-message-${message.type}`}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
          </div>
        )}

        {/* Date Range Filter */}
        <div className="ca-date-filter">
          <label>
            Start Date:
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            />
          </label>
          <label>
            End Date:
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </label>
          <button className="ca-btn" onClick={handleExportWorkbook} disabled={loading}>
            Export Excel Workbook
          </button>
        </div>

        {/* Main Tabs */}
        <div className="ca-tabs">
          <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>Dashboard</button>
          <button className={activeTab === 'gstr1' ? 'active' : ''} onClick={() => setActiveTab('gstr1')}>GSTR-1</button>
          <button className={activeTab === 'gstr3b' ? 'active' : ''} onClick={() => setActiveTab('gstr3b')}>GSTR-3B</button>
          <button className={activeTab === 'tds' ? 'active' : ''} onClick={() => setActiveTab('tds')}>TDS Register</button>
          <button className={activeTab === 'hsnSac' ? 'active' : ''} onClick={() => setActiveTab('hsnSac')}>HSN/SAC</button>
          <button className={activeTab === 'registers' ? 'active' : ''} onClick={() => setActiveTab('registers')}>Registers</button>
          <button className={activeTab === 'settlement' ? 'active' : ''} onClick={() => setActiveTab('settlement')}>Settlement</button>
          <button className={activeTab === 'roundOff' ? 'active' : ''} onClick={() => setActiveTab('roundOff')}>Round-off</button>
          <button className={activeTab === 'revenue' ? 'active' : ''} onClick={() => setActiveTab('revenue')}>Revenue</button>
          <button className={activeTab === 'financials' ? 'active' : ''} onClick={() => setActiveTab('financials')}>Financials</button>
        </div>

        {/* Loading Indicator */}
        {loading && <div className="ca-loading">Loading...</div>}

        {/* Tab Content */}
        <div className="ca-tab-content">
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'gstr1' && renderGSTR1()}
          {activeTab === 'gstr3b' && renderGSTR3B()}
          {activeTab === 'tds' && renderTDS()}
          {activeTab === 'hsnSac' && renderHSNSAC()}
          {activeTab === 'registers' && renderRegisters()}
          {activeTab === 'settlement' && renderSettlement()}
          {activeTab === 'roundOff' && renderRoundOff()}
          {activeTab === 'revenue' && renderRevenue()}
          {activeTab === 'financials' && renderFinancials()}
        </div>
      </div>
    </div>
  );
};

export default CAAuditReports;
